/**
 * Saying what a file's columns mean — the one decision no software may make.
 *
 * Field-first, not column-first: a real register is eighty-six columns, most
 * carrying no decision at all, and one row per column buried the two that did.
 */

import { useFollowRecordModule, useModule } from '../modules/useModule';
import { useFileQuery } from '../../queries/intake';
import { useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Box, Button, ToggleButton, ToggleButtonGroup } from '@mui/material';
import { useToast } from '../../provider/toast';
import {
	useClientLayoutsQuery,
	useConfirmMappingMutation,
	useMappingQuery,
} from '../../queries/firm';
import { PageHeader } from '../../provider/pageTitle';
import { useClientCrumbs } from '../intake/crumbs';
import { Card, Dialog, ErrorNotice, Note } from '../../shared/ui';
import { Select } from '../../shared/fields';
import { DataTable, type Column } from '../shared/tables';
import { ErrorState, LoadingState } from '../shared/states';
import { count } from '../../utils/format';
import type {
	CanonicalField,
	MappingView,
	ProposedColumn,
} from '../../schemas';

/** The word in the "How" cell. */
const BASIS_WORD: Record<string, string> = {
	CONFIRMED: 'Confirmed',
	EXACT: 'Same name',
	ALIAS: 'Known alternative name',
	SUGGESTED: 'Suggested',
	UNMAPPED: 'Not mapped',
	/* Task A2 — a distinct word from "You chose": nobody on this file chose
     it, a person chose it once on a DIFFERENT file with this exact header
     set, and the reason (in the badge's title) names which one. */
	REMEMBERED: 'From saved layout',
};

interface FieldRow {
	field: CanonicalField;
	/** The column supplying it. */
	column: ProposedColumn | null;
	/** A heading recognised for this field and deliberately not applied. */
	awaiting: ProposedColumn | null;
	basis: keyof typeof BASIS_WORD;
}

/** Required blocks processing; recommended feeds analyses; optional is extra. */
function groupOf(field: CanonicalField): string {
	if (field.required) return 'Required';
	if (field.tier === 'CRITICAL' || field.tier === 'STRUCTURAL')
		return 'Recommended';
	return 'Optional';
}

/**
 * The server answers per COLUMN; this screen asks per FIELD.
 *
 * Turning one into the other here, rather than asking the API for both shapes,
 * keeps a single source of truth for what is mapped — the confirmed mapping —
 * and leaves this file free to change how it reads without a server change.
 */
function buildRows(view: MappingView, draft: Map<string, string>): FieldRow[] {
	const byField = new Map<string, ProposedColumn>();
	const awaitingByField = new Map<string, ProposedColumn>();

	for (const column of view.columns) {
		const chosen = draft.get(column.sourceColumn) ?? column.canonicalField;
		if (chosen) {
			if (!byField.has(chosen)) byField.set(chosen, column);
		} else if (column.needsConfirmation && column.suggestion) {
			if (!awaitingByField.has(column.suggestion)) {
				awaitingByField.set(column.suggestion, column);
			}
		}
	}

	return view.canonicalFields
		.map((field) => {
			const column = byField.get(field.field) ?? null;
			const awaiting = awaitingByField.get(field.field) ?? null;
			const basis: FieldRow['basis'] = column
				? draft.has(column.sourceColumn)
					? 'CONFIRMED'
					: column.basis
				: awaiting
					? 'SUGGESTED'
					: 'UNMAPPED';
			return { field, column, awaiting, basis };
		})
		.filter(
			(row) =>
				row.field.required ||
				row.field.tier !== 'DESCRIPTIVE' ||
				row.column,
		);
}

export default function MappingWorkspace() {
	const { clientId = '', fileId = '' } = useParams();
	const navigate = useNavigate();
	/* The file's module wins over the URL's, for the badge and every link. */
	const fileModule = useFileQuery(fileId || null).data?.module;
	useFollowRecordModule(fileModule);
	const clientCrumbs = useClientCrumbs(clientId, fileModule);
	const { link } = useModule();

	/* Task 3 — apply a saved layout explicitly. `null` means "this file's own
     resolved layout", which is what the mapping endpoint uses automatically
     when the file has no confirmed mapping of its own. Picking one of this
     client's OTHER layouts here re-asks the same endpoint with `layoutId` in
     the query string, previewing that layout's mapping without saving
     anything — Confirm is still the only thing that persists it. */
	const [appliedLayoutId, setAppliedLayoutId] = useState<string | null>(null);
	const layouts = useClientLayoutsQuery(clientId ?? null);
	const mappingPath = appliedLayoutId
		? `/files/${fileId}/mapping?layoutId=${encodeURIComponent(appliedLayoutId)}`
		: `/files/${fileId}/mapping`;
	const mapping = useMappingQuery(mappingPath);

	/** This session's decisions, keyed by SOURCE COLUMN — the shape that makes a
      field impossible to claim twice. Applied over what the server returned. */
	const [draft, setDraft] = useState<Map<string, string>>(new Map());
	const [showMapped, setShowMapped] = useState(false);
	const [showNeeded, setShowNeeded] = useState(false);
	const [showAll, setShowAll] = useState(false);
	const [showConfirmPopup, setShowConfirmPopup] = useState(false);
	/** Task 2 — a name. `null` until a person types, so the input can show the
	 *  layout's own honest default (`view.layout.name`) as its value rather
	 *  than as a placeholder nobody reads. */
	const [layoutNameEdit, setLayoutNameEdit] = useState<string | null>(null);

	const toast = useToast();
	const confirmMutation = useConfirmMappingMutation(fileId ?? '');

	const confirm = {
		isPending: confirmMutation.isPending,
		error: confirmMutation.error,
		run: (fields: Array<[string, string]>, layoutName?: string) =>
			confirmMutation.mutate(
				{
					fields: fields.map(([sourceColumn, canonicalField]) => ({
						sourceColumn,
						canonicalField,
					})),
					...(layoutName ? { layoutName } : {}),
				},
				{
					onSuccess: (result) => {
						/* The conversion it started, so the next screen can show its progress. */
						const runId = (
							result as
								{ normaliseRunId?: string | null } | undefined
						)?.normaliseRunId;
						/* An H2R file has no classification: its rows are processed and it
               is ready for audits, so it goes to the processing screen, never
               to the rules review. */
						if (mapping.data?.module === 'H2R') {
							toast(
								'ok',
								'Mapping confirmed. The rows are being processed.',
							);
							navigate(
								link(
									`/intake/clients/${clientId}/files/${fileId}/processing${
										runId
											? `?run=${encodeURIComponent(runId)}`
											: ''
									}`,
								),
							);
							return;
						}
						toast(
							'ok',
							'Mapping confirmed. Choose the rules that will classify the rows.',
						);
						/* Not the file page: the mapping is what makes rules evaluable at
               all, so this is the first moment the review step can exist — and
               the last one before processing writes the rows. */
						navigate(
							link(
								`/intake/clients/${clientId}/files/${fileId}/review${
									runId
										? `?normalise=${encodeURIComponent(runId)}`
										: ''
								}`,
							),
						);
					},
				},
			),
	};

	const rows = useMemo(
		() => (mapping.data ? buildRows(mapping.data, draft) : []),
		[mapping.data, draft],
	);

	/* Search + status filter + sort for the field table below — client-side
     only, over `rows`, so "which columns are left to map" is a filter click
     instead of a scroll-and-scan. */
	const [query, setQuery] = useState('');
	const [statusFilter, setStatusFilter] = useState<
		'all' | 'needs' | 'mapped'
	>('all');

	const visibleRows = useMemo(() => {
		const q = query.trim().toLowerCase();
		let next = rows;
		if (q) {
			next = next.filter(
				(row) =>
					row.field.label.toLowerCase().includes(q) ||
					row.field.field.toLowerCase().includes(q) ||
					(row.column?.sourceColumn.toLowerCase().includes(q) ??
						false),
			);
		}
		if (statusFilter === 'needs') {
			next = next.filter(
				(row) => row.basis === 'SUGGESTED' || row.basis === 'UNMAPPED',
			);
		} else if (statusFilter === 'mapped') {
			next = next.filter((row) => row.column !== null);
		}
		/* The fields blocking processing (required, still unmapped) float to the
       top; a header click in the table re-sorts from there. */
		const priority = (row: (typeof next)[number]) =>
			row.field.required && !row.column ? 0 : row.field.required ? 1 : 2;
		next = [...next].sort((a, b) => priority(a) - priority(b));
		return next;
	}, [rows, query, statusFilter]);

	/* Same idea, for the "All columns" drawer — a 64-row list needs a search
     box more than it needs a sticky header alone. */
	const [allQuery, setAllQuery] = useState('');

	if (mapping.isPending)
		return <LoadingState label="Loading the file's columns…" />;
	if (mapping.error)
		return <ErrorState error={mapping.error} onRetry={mapping.refetch} />;
	if (!mapping.data) return null;

	const view = mapping.data;

	/** Assign a field to a column, releasing whichever column held it before. */
	const assign = (column: string, field: string) => {
		setDraft((current) => {
			const next = new Map(current);
			for (const [key, value] of next) {
				if (value === field && key !== column) next.set(key, '');
			}
			/* The server's own proposal also has to be released, or two columns
         claim one field and the confirm is refused by a unique constraint
         nobody can read. */
			for (const proposed of view.columns) {
				const effective =
					next.get(proposed.sourceColumn) ?? proposed.canonicalField;
				if (effective === field && proposed.sourceColumn !== column) {
					next.set(proposed.sourceColumn, '');
				}
			}
			next.set(column, field);
			return next;
		});
	};

	const clear = (column: string) => {
		setDraft((current) => new Map(current).set(column, ''));
	};

	/** What would be sent: every column with an effective field, in one list. */
	const decided: Array<[string, string]> = view.columns
		.map((column) => {
			const field =
				draft.get(column.sourceColumn) ?? column.canonicalField ?? '';
			return [column.sourceColumn, field] as [string, string];
		})
		.filter(([, field]) => field !== '');

	const mappedFields = new Set(decided.map(([, field]) => field));
	const requiredMissing = view.canonicalFields
		.filter((field) => field.required && !mappedFields.has(field.field))
		.map((field) => field.field);

	const required = rows.filter((row) => row.field.required);
	const ready = required.filter((row) => row.column);
	const outstanding = rows.filter((row) => !row.column && row.field.required);

	const columnOptions = [
		{ value: '', label: 'Not set' },
		...view.columns.map((column) => ({
			value: column.sourceColumn,
			label: column.samples[0]
				? `${column.sourceColumn} — ${column.samples[0]}`
				: column.sourceColumn,
			hint:
				column.samples.length > 0
					? column.samples.join(' · ')
					: 'no values in the sample',
		})),
	];

	const byColumnName = new Map(
		view.columns.map((column) => [column.sourceColumn, column]),
	);
	const usedColumns = new Set(decided.map(([column]) => column));
	const spare = view.columns.length - usedColumns.size;

	/* Task A2 — reuse must never hide that it happened. Counted off `rows`
     (per FIELD, post-draft) rather than `view.columns` so the banner tracks
     what is STILL remembered as a person edits, not what arrived that way. */
	const rememberedActive = rows.filter(
		(row) => row.basis === 'REMEMBERED',
	).length;

	/* Task 3 — only layouts with a confirmed mapping behind them are worth
     offering: applying an empty one would change nothing and only confuse. */
	const layoutOptions = (layouts.data ?? [])
		.filter((layout) => layout.confirmedMapping)
		.map((layout) => ({
			value: layout.pkLayoutId,
			label: layout.name,
			hint: `From "${layout.confirmedMapping!.fileName}" · mapping v${layout.confirmedMapping!.version}`,
		}));

	const layoutNameValue = layoutNameEdit ?? view.layout?.name ?? '';

	/* For the "All columns" drawer — a 64-row list needs a search box more
     than it needs a sticky header alone. */
	const allRows = (() => {
		const q = allQuery.trim().toLowerCase();
		let list = view.columns.map((column) => {
			const field =
				draft.get(column.sourceColumn) ?? column.canonicalField;
			const label = field
				? (view.canonicalFields.find((f) => f.field === field)?.label ??
					field)
				: null;
			const state =
				label ??
				(column.needsConfirmation ? 'Awaiting you' : 'Not needed');
			return { column, label, state };
		});
		if (q) {
			list = list.filter(
				(item) =>
					item.column.sourceColumn.toLowerCase().includes(q) ||
					item.state.toLowerCase().includes(q),
			);
		}
		return list;
	})();

	type AllRow = (typeof allRows)[number];
	type FieldRowShape = (typeof rows)[number];

	const mappedColumns: Array<Column<[string, string]>> = [
		{
			key: 'column',
			label: 'Column in the file',
			sortValue: ([column]) => column,
			render: ([column]) => (
				<Box component="span" className="b">
					{column}
				</Box>
			),
		},
		{
			key: 'field',
			label: 'Standard field',
			render: ([, field]) =>
				view.canonicalFields.find((f) => f.field === field)?.label ??
				field,
		},
	];

	const neededColumns: Array<Column<FieldRowShape>> = [
		{
			key: 'field',
			label: 'Standard field',
			render: (row) => (
				<Box component="span" className="b" title={row.field.help}>
					{row.field.label}
				</Box>
			),
		},
		{
			key: 'suggested',
			label: 'Suggested column',
			/* The heading recognised and deliberately not applied — naming it turns
         "still needed" into a to-do with an answer. */
			render: (row) =>
				row.awaiting ? (
					<Box component="span" className="small">
						{row.awaiting.sourceColumn}
						{row.awaiting.samples[0] && (
							<Box component="span" className="mut">
								{' '}
								— {row.awaiting.samples[0]}
							</Box>
						)}
					</Box>
				) : (
					<Box component="span" className="mut">
						No column recognised
					</Box>
				),
		},
	];

	const allColumns: Array<Column<AllRow>> = [
		{
			key: 'column',
			label: 'Column',
			sortValue: (row) => row.column.sourceColumn,
			render: (row) => (
				<Box component="span" className="b">
					{row.column.sourceColumn}
				</Box>
			),
		},
		{
			key: 'example',
			label: 'Example',
			render: (row) => (
				<Box component="span" className="mut small">
					{row.column.samples[0] ?? '—'}
				</Box>
			),
		},
		{
			key: 'state',
			label: 'Used as',
			width: 190,
			sortValue: (row) => row.state,
			render: (row) =>
				row.label ? (
					<Box component="span" className="small">
						{row.label}
					</Box>
				) : row.column.needsConfirmation ? (
					<Box component="span" className="bdg warning plain">
						Needs a decision
					</Box>
				) : (
					/* Nothing failed — no analysis reads this column. */
					<Box component="span" className="mut">
						Not used
					</Box>
				),
		},
	];

	const fieldColumns: Array<Column<FieldRowShape>> = [
		{
			key: 'field',
			label: 'Standard field',
			width: 230,
			sortValue: (row) => row.field.label,
			render: (row) => (
				<Box className="stack2">
					<Box component="span" className="b" title={row.field.help}>
						{row.field.label}
					</Box>
					<Box className="l2 mut">
						{groupOf(row.field)}
						{row.field.type === 'money' && (
							<Box component="span" className="text-warn">
								{' '}
								· always chosen by a person
							</Box>
						)}
					</Box>
				</Box>
			),
		},
		{
			key: 'column',
			label: 'From this file',
			width: 300,
			render: (row) => (
				<Select
					className="sm"
					value={row.column?.sourceColumn ?? ''}
					ariaLabel={`Column for ${row.field.label}`}
					/* A field with nothing chosen shows the guess as the placeholder,
             never as a pre-selected value. */
					options={
						row.column ? columnOptions : columnOptions.slice(1)
					}
					placeholder={
						row.awaiting
							? `Looks like ${row.awaiting.sourceColumn} — choose`
							: 'Not mapped'
					}
					searchable={view.columns.length > 8}
					searchPlaceholder="Search columns…"
					onChange={(column) => {
						if (column) assign(column, row.field.field);
						else if (row.column) clear(row.column.sourceColumn);
					}}
				/>
			),
		},
		{
			key: 'how',
			label: 'How it was matched',
			width: 170,
			sortValue: (row) => BASIS_WORD[row.basis] ?? row.basis,
			render: (row) => (
				<Box
					component="span"
					title={row.column?.reason}
					className={`bdg plain ${
						row.basis === 'UNMAPPED'
							? row.field.required
								? 'warning'
								: 'gray'
							: row.basis === 'SUGGESTED'
								? 'warning'
								: row.basis === 'REMEMBERED'
									? 'info'
									: 'clean'
					}`}
				>
					{BASIS_WORD[row.basis] ?? row.basis}
				</Box>
			),
		},
		{
			key: 'help',
			label: 'What it is used for',
			width: 230,
			render: (row) => (
				<Box component="span" className="small mut">
					{row.field.help}
				</Box>
			),
		},
	];

	return (
		<div className="stack">
			<PageHeader
				breadcrumbs={[
					...clientCrumbs,
					{
						label: view.fileName,
						to: `/intake/clients/${clientId}/files/${fileId}`,
					},
				]}
				title="Column mapping"
				subtitle={
					<>
						{ready.length} of {required.length} required fields
						mapped · {count(view.columns.length)} columns in the
						file
					</>
				}
				actions={
					<div
						className="row"
						style={{ gap: 'var(--s2)', alignItems: 'center' }}
					>
						{view.layout && (
							<input
								type="text"
								className="ctl sm"
								style={{ width: 200 }}
								value={layoutNameValue}
								aria-label="Name this layout"
								placeholder="Name this layout"
								title="Future files from this client with the same columns will use this mapping."
								onChange={(event) =>
									setLayoutNameEdit(event.target.value)
								}
							/>
						)}
						<Button
							loading={confirm.isPending}
							variant="contained"
							size="small"

							disabled={
								requiredMissing.length > 0 || confirm.isPending
							}
							title={
								requiredMissing.length > 0
									? `Still unmapped: ${requiredMissing.join(', ')}`
									: undefined
							}
							onClick={() => setShowConfirmPopup(true)}
						>
							Confirm mapping
						</Button>
					</div>
				}
			/>

			<div className="pagebar">
				{view.layout && (
					<span className="small mut">
						Saved layout: {view.layout.name}
					</span>
				)}
			</div>

			{layoutOptions.length > 0 && (
				<div
					className="row"
					style={{
						gap: 'var(--s2)',
						alignItems: 'center',
						flexWrap: 'wrap',
					}}
				>
					<span className="small mut">Apply a saved layout:</span>
					<Select
						className="sm"
						value={appliedLayoutId ?? ''}
						ariaLabel="Apply a saved layout"
						options={[
							{ value: '', label: "This file's own layout" },
							...layoutOptions,
						]}
						placeholder="This file's own layout"
						searchable={layoutOptions.length > 8}
						searchPlaceholder="Search layouts…"
						onChange={(next) => {
							setAppliedLayoutId(next || null);
							/* A stale local edit made against the PREVIOUS preview would
                 otherwise silently survive the switch and could look like it
                 came from the newly applied layout. */
							setDraft(new Map());
						}}
					/>
					{appliedLayoutId && (
						<span className="small mut">
							Previewing — nothing is saved until you confirm.
						</span>
					)}
				</div>
			)}

			{rememberedActive > 0 && view.remembered && (
				<Note
					tone="info"
					title={`${rememberedActive} column${rememberedActive === 1 ? '' : 's'} remembered from "${view.remembered.fromFileName}"`}
					row
				>
					Confirmed there (mapping v{view.remembered.fromVersion})
					with this exact header set
					{view.remembered.explicit
						? ' — applied from the saved layout you picked'
						: ''}
					. Review before confirming; any of them can still be changed
					below.
				</Note>
			)}

			<ErrorNotice error={confirm.error} />

			<Card
				title="Standard fields"
				hint={`${ready.length} of ${required.length} required fields have a column`}
				actions={
					<Box
						className="row"
						style={{ gap: 'var(--s2)', flexWrap: 'wrap' }}
					>
						<Button
							variant="outlined"
							size="small"
							onClick={() => setShowMapped(true)}
						>
							Mapped columns
							<span className="n">{usedColumns.size}</span>
						</Button>
						<Button
							variant="outlined"
							size="small"
							onClick={() => setShowNeeded(true)}
						>
							Still needed
							{outstanding.length > 0 && (
								<span className="n warn">
									{outstanding.length}
								</span>
							)}
						</Button>
						<Button
							variant="outlined"
							size="small"
							onClick={() => setShowAll(true)}
						>
							All {count(view.columns.length)} columns
						</Button>
					</Box>
				}
			>
				<Box className="stack">
					{requiredMissing.length === 0 ? (
						<Note
							tone="ok"
							title="Every required field has a column"
							row
						>
							{spare} other columns in this file are not used.
							That is expected.
						</Note>
					) : (
						<Note
							tone="warn"
							title={`Still needed: ${requiredMissing.join(', ')}`}
							row
						>
							Choose a column for each before confirming. Money
							and identity fields are always chosen by a person.
						</Note>
					)}

					<Box
						className="row"
						style={{
							gap: 'var(--s2)',
							flexWrap: 'wrap',
							alignItems: 'center',
						}}
					>
						<input
							type="text"
							className="ctl sm"
							style={{ width: 220 }}
							placeholder="Search fields or columns…"
							aria-label="Search fields or columns"
							value={query}
							onChange={(event) => setQuery(event.target.value)}
						/>
						<ToggleButtonGroup
							size="small"
							exclusive
							value={statusFilter}
							aria-label="Show fields"
							onChange={(_event, next) =>
								next && setStatusFilter(next)
							}
						>
							<ToggleButton value="all">All</ToggleButton>
							<ToggleButton value="needs">
								Needs attention
							</ToggleButton>
							<ToggleButton value="mapped">Mapped</ToggleButton>
						</ToggleButtonGroup>
						{(query || statusFilter !== 'all') && (
							<Box component="span" className="small mut">
								{visibleRows.length} of {rows.length} fields
							</Box>
						)}
					</Box>

					<DataTable
						columns={fieldColumns}
						rows={visibleRows}
						rowKey={(row) => row.field.field}
						caption="Standard fields"
						empty="No fields match this search."
					/>
				</Box>
			</Card>

			<Dialog
				open={showMapped}
				onClose={() => setShowMapped(false)}
				title="Mapped columns"
				subtitle={`${decided.length} of this file's columns are used`}
			>
				<DataTable
					columns={mappedColumns}
					rows={decided}
					rowKey={([column]) => column}
					caption="Mapped columns"
					empty="Nothing is mapped yet."
					maxHeight="compact"
				/>
			</Dialog>

			<Dialog
				open={showNeeded}
				onClose={() => setShowNeeded(false)}
				title="Still needed"
				subtitle="Required fields that no column supplies yet"
			>
				{outstanding.length === 0 ? (
					<Note tone="ok" title="Nothing outstanding" row>
						Every required field has a column.
					</Note>
				) : (
					<DataTable
						columns={neededColumns}
						rows={outstanding}
						rowKey={(row) => row.field.field}
						caption="Still needed"
						maxHeight="compact"
					/>
				)}
			</Dialog>

			<Dialog
				open={showAll}
				onClose={() => setShowAll(false)}
				title={`All ${count(view.columns.length)} columns`}
				subtitle={`Every heading in ${view.fileName}, and what became of it`}
			>
				<Box className="stack">
					<Note
						tone="gray"
						title={`${spare} columns are not used`}
						row
					>
						That is expected — a register carries data no analysis
						reads.
					</Note>
					<input
						type="text"
						className="ctl sm"
						style={{ width: 240 }}
						placeholder="Search columns…"
						aria-label="Search all columns"
						value={allQuery}
						onChange={(event) => setAllQuery(event.target.value)}
					/>
					<DataTable
						columns={allColumns}
						rows={allRows}
						rowKey={(row) => row.column.sourceColumn}
						caption="All columns"
						empty="No columns match this search."
						maxHeight="compact"
					/>
				</Box>
			</Dialog>

			{byColumnName.size === 0 && (
				<Note
					tone="danger"
					title="No columns were read from this file"
					row
				>
					No header row was found. Retry reading the file from its
					page, or ask the client for a fresh export.
				</Note>
			)}

			<Dialog
				open={showConfirmPopup}
				onClose={() => setShowConfirmPopup(false)}
				title="Confirm the column mapping?"
				subtitle={view.fileName}
				footer={
					<div className="spread">
						<Button
							variant="outlined"

							disabled={confirm.isPending}
							onClick={() => setShowConfirmPopup(false)}
						>
							Cancel
						</Button>
						<Button
							loading={confirm.isPending}
							variant="contained"

							disabled={confirm.isPending}
							onClick={() =>
								confirm.run(decided, layoutNameValue)
							}
						>
							Confirm mapping
						</Button>
					</div>
				}
			>
				<Box className="stack">
					<Box component="p" className="small">
						<b>{ready.length}</b> of <b>{required.length}</b>{' '}
						required fields are mapped, from <b>{decided.length}</b>{' '}
						of this file's columns.
					</Box>
					{rememberedActive > 0 && (
						<Box component="p" className="small mut">
							{rememberedActive} of those came from a remembered
							layout — review them above if you have not already.
						</Box>
					)}
					<Box component="p" className="small mut">
						Future files from this client with the same columns will
						use this mapping. Next: review the classification rules
						and process the file.
					</Box>
					<ErrorNotice error={confirm.error} />
				</Box>
			</Dialog>
		</div>
	);
}
